import { useEffect, useState } from "react";
import axios from "axios";
import { Printer } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { API, LOGO_URL } from "@/lib/brand";
import { TOKEN_KEY, authHeader, currentEdition, editionLabel, errText, stageOf } from "@/lib/admin";

const PRINT_CSS = `
@page { size: A4; margin: 10mm; }
@media print {
  .no-print { display: none !important; }
  body { background: #fff !important; }
  .label-sheet { gap: 4mm !important; padding: 0 !important; }
  .label { break-inside: avoid; box-shadow: none !important; }
}`;

const LabelCard = ({ r }) => (
  <div className="label bg-white border border-dashed border-stone-400 rounded-lg p-4 h-[44mm] flex flex-col justify-between overflow-hidden" data-testid={`label-${r.id}`}>
    <div>
      <p className="font-semibold text-[15px] leading-tight text-stone-900">{r.full_name}</p>
      <p className="text-[12.5px] leading-snug text-stone-800 mt-1">
        {r.address_line1}{r.address_line2 ? <><br />{r.address_line2}</> : null}<br />
        {r.city}, {r.state} – <span className="font-semibold tracking-wide">{r.pincode}</span>
      </p>
      <p className="text-[11.5px] text-stone-600 mt-1">Ph: {r.phone}</p>
    </div>
    <div className="flex items-center justify-between border-t border-stone-200 pt-1.5">
      <span className="font-hand text-[15px] text-stone-700">From The Little Lantern Club</span>
      <img src={LOGO_URL} alt="" className="h-9 w-auto object-contain" />
    </div>
  </div>
);

export default function Labels() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const edition = params.get("edition") || currentEdition();
  const stage = params.get("stage") || "all";

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setErr("Please sign in at /admin first."); return; }
    const plan = params.get("plan");
    axios.get(`${API}/admin/entries?edition=${edition}${plan ? `&plan=${plan}` : ""}`, authHeader(token))
      .then((r) => setRows(r.data.filter((x) => stage === "all" || stageOf(x) === stage)))
      .catch((ex) => setErr(errText(ex)));
  }, [params, edition, stage]);

  return (
    <div className="min-h-screen bg-stone-100" data-testid="labels-page">
      <style>{PRINT_CSS}</style>
      <div className="no-print max-w-[210mm] mx-auto px-4 pt-8 pb-4 flex items-center justify-between">
        <div>
          <p className="font-serif-display text-3xl">Envelope labels</p>
          <p className="text-sm text-stone-600" data-testid="labels-summary">{editionLabel(edition)} · {err ? "unavailable" : rows ? `${rows.length} label${rows.length === 1 ? "" : "s"}` : "loading"} · A4, 2 per row</p>
        </div>
        <button onClick={() => window.print()} disabled={!rows?.length} className="rounded-full bg-stone-900 text-white px-6 py-3 text-sm flex items-center gap-2 hover:bg-amber-600 transition-colors disabled:opacity-40" data-testid="labels-print-button"><Printer className="h-4 w-4" />Print</button>
      </div>
      {err && <p className="no-print text-center text-red-700 mt-10" data-testid="labels-error">{err}</p>}
      <div className="label-sheet max-w-[210mm] mx-auto grid grid-cols-2 gap-4 p-4 pb-16">
        {(rows || []).map((r) => <LabelCard key={r.id} r={r} />)}
      </div>
    </div>
  );
}
